/* eslint-disable max-len */
import {
    useState, useMemo, useCallback, type CSSProperties,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import {
    ChevronDownIcon, ChevronRightIcon, LockClosedIcon, ArrowLeftIcon, TranslateIcon,
} from '@heroicons/react/solid';
import axios, { AxiosError } from 'axios';
import env from 'src/env/index';
import {
    apiSpec, supportedLanguages, type Endpoint, type PropertySchema, type HttpMethod,
} from './openApiSpec';

type ResponseData = {
    status: number;
    statusText: string;
    body: string;
    duration: number;
};

type MethodStyle = {
    badgeBg: string;
    bg: string;
    border: string;
    text: string;
};

const METHOD_STYLES: Record<HttpMethod, MethodStyle> = {
    GET: {
        badgeBg: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8',
    },
    POST: {
        badgeBg: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d',
    },
    PUT: {
        badgeBg: '#f59e0b', bg: '#fffbeb', border: '#fde68a', text: '#b45309',
    },
    DELETE: {
        badgeBg: '#ef4444', bg: '#fef2f2', border: '#fecaca', text: '#b91c1c',
    },
};

const getStatusColor = (code: string): string => {
    if (code.startsWith('2')) return '#16a34a';
    if (code.startsWith('4')) return '#d97706';
    if (code.startsWith('5')) return '#dc2626';
    return '#6b7280';
};

const generateExampleValue = (prop: PropertySchema): unknown => {
    if (prop.examples && prop.examples.length > 0) return prop.examples[0];
    if (prop.enum && prop.enum.length > 0) return prop.enum[0];
    switch (prop.type) {
        case 'string':
            if (prop.format === 'uuid') return '3fa85f64-5717-4562-b3fc-2c963f66afa6';
            if (prop.format === 'email') return 'user@example.com';
            return 'string';
        case 'integer':
        case 'number':
            return 0;
        case 'boolean':
            return true;
        case 'object':
            if (prop.properties) {
                return Object.fromEntries(
                    Object.entries(prop.properties).map(([k, v]) => [k, generateExampleValue(v)])
                );
            }
            return {};
        case 'array':
            if (prop.items) return [generateExampleValue(prop.items)];
            return [];
        default:
            return null;
    }
};

const generateExampleBody = (schema: PropertySchema): string => {
    const example = Object.entries(schema.properties ?? {}).reduce<Record<string, unknown>>(
        (acc, [key, prop]) => ({
            ...acc,
            [key]: generateExampleValue(prop),
        }),
        {}
    );
    return JSON.stringify(example, null, 2);
};

const buildCurl = (
    method: string, url: string, bearerToken: string,
    mediaType: string | undefined, body: string | undefined
): string => {
    const parts = [`curl -X ${method} '${url}'`];
    if (bearerToken) {
        parts.push(`  -H 'Authorization: Bearer ${bearerToken}'`);
    }
    if (mediaType && body) {
        parts.push(`  -H 'Content-Type: ${mediaType}'`);
        parts.push(`  -d '${body}'`);
    }
    return parts.join(' \\\n');
};

const formatType = (prop: PropertySchema): string => {
    let result = prop.type;
    if (prop.format) result += ` ($${prop.format})`;
    if (prop.enum) result += ` enum: [${prop.enum.join(', ')}]`;
    return result;
};

const SchemaTree = ({ schema, depth = 0 }: { schema: PropertySchema; depth?: number }) => {
    const intl = useIntl();
    const [collapsed, setCollapsed] = useState(depth > 1);
    const indent = depth * 16;

    if (schema.type === 'array' && schema.items) {
        return (
            <div>
                <span className="font-mono text-xs" style={{ color: '#6b7280' }}>
                    {'array ['}
                </span>
                <SchemaTree schema={schema.items} depth={depth} />
                <span className="font-mono text-xs" style={{ color: '#6b7280' }}>
                    {']'}
                </span>
            </div>
        );
    }

    if (schema.type === 'object' && schema.properties) {
        const entries = Object.entries(schema.properties);
        if (entries.length === 0) {
            return <span className="font-mono text-xs" style={{ color: '#6b7280' }}>{'{ }'}</span>;
        }

        return (
            <div>
                <button
                    type="button"
                    onClick={() => setCollapsed(!collapsed)}
                    className="font-mono text-xs flex items-center gap-1"
                    style={{
                        color: '#6b7280', paddingLeft: depth > 0 ? indent : 0, background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    }}
                >
                    {collapsed ? (
                        <ChevronRightIcon style={{ width: 12, height: 12 }} />
                    ) : (
                        <ChevronDownIcon style={{ width: 12, height: 12 }} />
                    )}
                    <span>{'{'}</span>
                    {collapsed && <span style={{ color: '#9ca3af' }}>{` ${intl.formatMessage({ id: 'LP_API_DOC_PROPERTIES_COUNT', defaultMessage: '{count} properties' }, { count: entries.length })} `}</span>}
                    {collapsed && <span>{'}'}</span>}
                </button>
                {!collapsed && (
                    <div style={{ paddingLeft: 16 }}>
                        {entries.map(([key, prop]) => {
                            const isRequired = prop.required
                                || schema.requiredFields?.includes(key);
                            return (
                                <div key={key} className="py-0.5">
                                    <span className="font-mono text-xs">
                                        <span style={{ color: '#1e40af' }}>{key}</span>
                                        {isRequired && <span style={{ color: '#dc2626' }}>*</span>}
                                        <span style={{ color: '#6b7280' }}>{': '}</span>
                                        {(prop.type === 'object' && prop.properties) || (prop.type === 'array' && prop.items) ? (
                                            <SchemaTree schema={prop} depth={depth + 1} />
                                        ) : (
                                            <>
                                                <span style={{ color: '#059669' }}>{prop.type}</span>
                                                {prop.format && (
                                                    <span style={{ color: '#9ca3af' }}>{` ($${prop.format})`}</span>
                                                )}
                                                {prop.enum && (
                                                    <span style={{ color: '#9ca3af' }}>
                                                        {` enum: [${prop.enum.join(', ')}]`}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                        {prop.description && (
                                            <span style={{ color: '#9ca3af' }}>{`  // ${prop.description}`}</span>
                                        )}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
                {!collapsed && (
                    <span className="font-mono text-xs" style={{ color: '#6b7280' }}>{'}'}</span>
                )}
            </div>
        );
    }

    return (
        <span className="font-mono text-xs">
            <span style={{ color: '#059669' }}>{schema.type}</span>
            {schema.format && <span style={{ color: '#9ca3af' }}>{` ($${schema.format})`}</span>}
            {schema.enum && (
                <span style={{ color: '#9ca3af' }}>{` enum: [${schema.enum.join(', ')}]`}</span>
            )}
        </span>
    );
};

const ApiDocumentationPage = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const [serverUrl, setServerUrl] = useState(env.openApiUrl);
    const [bearerToken, setBearerToken] = useState('');
    const [showAuth, setShowAuth] = useState(false);
    const [showLanguages, setShowLanguages] = useState(false);
    const [expandedEndpoints, setExpandedEndpoints] = useState<Set<string>>(new Set());
    const [expandedTags, setExpandedTags] = useState<Set<string>>(
        new Set(['Authentication', 'Participant', 'Questionnaire', 'Portrait'])
    );
    const [tryItOutActive, setTryItOutActive] = useState<Set<string>>(new Set());
    const [paramValues, setParamValues] = useState<Record<string, Record<string, string>>>({});
    const [bodyValues, setBodyValues] = useState<Record<string, string>>({});
    const [responses, setResponses] = useState<Record<string, ResponseData>>({});
    const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

    const endpointsByTag = useMemo(() => {
        const grouped: Record<string, Endpoint[]> = {};
        apiSpec.endpoints.forEach((ep) => {
            if (!grouped[ep.tag]) grouped[ep.tag] = [];
            grouped[ep.tag].push(ep);
        });
        return grouped;
    }, []);

    const tags = useMemo(() => {
        const seen = new Set<string>();
        return apiSpec.endpoints.reduce<string[]>((acc, ep) => {
            if (!seen.has(ep.tag)) {
                seen.add(ep.tag);
                acc.push(ep.tag);
            }
            return acc;
        }, []);
    }, []);

    const toggleEndpoint = useCallback((id: string) => {
        setExpandedEndpoints((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleTag = useCallback((tag: string) => {
        setExpandedTags((prev) => {
            const next = new Set(prev);
            if (next.has(tag)) next.delete(tag);
            else next.add(tag);
            return next;
        });
    }, []);

    const activateTryItOut = useCallback((endpoint: Endpoint) => {
        setTryItOutActive((prev) => {
            const next = new Set(prev);
            if (next.has(endpoint.id)) {
                next.delete(endpoint.id);
            } else {
                next.add(endpoint.id);
                if (endpoint.requestBody && !bodyValues[endpoint.id]) {
                    setBodyValues((bv) => ({
                        ...bv,
                        [endpoint.id]: generateExampleBody(endpoint.requestBody!.schema),
                    }));
                }
            }
            return next;
        });
    }, [bodyValues]);

    const updateParam = useCallback((endpointId: string, paramName: string, value: string) => {
        setParamValues((prev) => ({
            ...prev,
            [endpointId]: { ...prev[endpointId], [paramName]: value },
        }));
    }, []);

    const executeRequest = useCallback(async (endpoint: Endpoint) => {
        setLoadingIds((prev) => new Set(prev).add(endpoint.id));
        setResponses((prev) => { const next = { ...prev }; delete next[endpoint.id]; return next; });

        let url = `${serverUrl}${endpoint.path}`;
        const params = paramValues[endpoint.id] || {};

        endpoint.parameters?.filter((p) => p.in === 'path').forEach((p) => {
            url = url.replace(`{${p.name}}`, encodeURIComponent(params[p.name] || ''));
        });

        const queryParams = endpoint.parameters
            ?.filter((p) => p.in === 'query' && params[p.name])
            .map((p) => `${p.name}=${encodeURIComponent(params[p.name])}`)
            .join('&');
        if (queryParams) url += `?${queryParams}`;

        const headers: Record<string, string> = {};
        if (endpoint.requiresAuth && bearerToken) {
            headers.Authorization = `Bearer ${bearerToken}`;
        }
        if (endpoint.requestBody) {
            headers['Content-Type'] = endpoint.requestBody.mediaType;
        }

        let data: unknown;
        if (endpoint.requestBody && bodyValues[endpoint.id]) {
            try {
                data = JSON.parse(bodyValues[endpoint.id]);
            } catch {
                data = bodyValues[endpoint.id];
            }
        }

        const startTime = Date.now();
        try {
            const response = await axios({
                method: endpoint.method.toLowerCase(),
                url,
                headers,
                data,
                validateStatus: () => true,
            });
            const duration = Date.now() - startTime;
            const body = typeof response.data === 'object'
                ? JSON.stringify(response.data, null, 2)
                : String(response.data);
            setResponses((prev) => ({
                ...prev,
                [endpoint.id]: {
                    status: response.status,
                    statusText: response.statusText,
                    body,
                    duration,
                },
            }));
        } catch (error) {
            const duration = Date.now() - startTime;
            const axiosErr = error as AxiosError;
            setResponses((prev) => ({
                ...prev,
                [endpoint.id]: {
                    status: axiosErr.response?.status ?? 0,
                    statusText: axiosErr.response?.statusText ?? intl.formatMessage({ id: 'LP_API_DOC_NETWORK_ERROR', defaultMessage: 'Network Error' }),
                    body: axiosErr.message || intl.formatMessage({ id: 'LP_API_DOC_REQUEST_FAILED', defaultMessage: 'Request failed. This may be due to CORS restrictions or network issues.' }),
                    duration,
                },
            }));
        } finally {
            setLoadingIds((prev) => {
                const next = new Set(prev);
                next.delete(endpoint.id);
                return next;
            });
        }
    }, [serverUrl, bearerToken, paramValues, bodyValues, intl]);

    const buildEndpointUrl = useCallback((endpoint: Endpoint) => {
        let url = `${serverUrl}${endpoint.path}`;
        const params = paramValues[endpoint.id] || {};
        endpoint.parameters?.filter((p) => p.in === 'path').forEach((p) => {
            url = url.replace(`{${p.name}}`, params[p.name] || `{${p.name}}`);
        });
        const queryParams = endpoint.parameters
            ?.filter((p) => p.in === 'query' && params[p.name])
            .map((p) => `${p.name}=${encodeURIComponent(params[p.name])}`)
            .join('&');
        if (queryParams) url += `?${queryParams}`;
        return url;
    }, [serverUrl, paramValues]);

    const cardStyle = (method: HttpMethod): CSSProperties => ({
        backgroundColor: METHOD_STYLES[method].bg,
        borderColor: METHOD_STYLES[method].border,
        borderWidth: '1px',
        borderLeftWidth: '4px',
        borderLeftColor: METHOD_STYLES[method].badgeBg,
    });

    const badgeStyle = (method: HttpMethod): CSSProperties => ({
        backgroundColor: METHOD_STYLES[method].badgeBg,
        color: '#ffffff',
        minWidth: '4.5rem',
    });

    return (
        <div className="pb-20">
            <button
                type="button"
                onClick={() => navigate('/developer')}
                className="flex items-center gap-2 text-sm font-medium"
                style={{
                    color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                }}
            >
                <ArrowLeftIcon style={{ width: 16, height: 16 }} />
                {intl.formatMessage({ id: 'LP_API_DOC_BACK_TO_DASHBOARD', defaultMessage: 'Back to Developer Dashboard' })}
            </button>

            <div className="mt-4 flex items-center gap-3 flex-wrap">
                <h1 className="text-headline-lg font-bold" style={{ lineHeight: '45px' }}>
                    {apiSpec.title}
                </h1>
                <span
                    className="text-xs font-semibold px-2 py-1 rounded"
                    style={{ backgroundColor: '#e0e7ff', color: '#3730a3' }}
                >
                    v{apiSpec.version}
                </span>
                <span
                    className="text-xs font-medium px-2 py-1 rounded"
                    style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}
                >
                    OAS {apiSpec.openapi}
                </span>
            </div>

            <div className="mt-6 flex flex-col gap-2" style={{ maxWidth: 600 }}>
                <label className="text-xs font-semibold" style={{ color: '#374151' }}>{intl.formatMessage({ id: 'LP_API_DOC_SERVER_URL', defaultMessage: 'Server URL' })}</label>
                <input
                    type="text"
                    value={serverUrl}
                    onChange={(e) => setServerUrl(e.target.value)}
                    className="px-3 py-2 rounded text-sm font-mono"
                    style={{ border: '1px solid #d1d5db', outline: 'none' }}
                />
            </div>

            <div className="mt-6">
                <button
                    type="button"
                    onClick={() => setShowAuth(!showAuth)}
                    className="flex items-center gap-2 px-4 py-2 rounded text-sm font-semibold"
                    style={{
                        border: '1px solid #d1d5db',
                        background: showAuth ? '#f9fafb' : '#ffffff',
                        cursor: 'pointer',
                    }}
                >
                    <LockClosedIcon style={{ width: 16, height: 16, color: '#16a34a' }} />
                    {intl.formatMessage({ id: 'LP_API_DOC_AUTHORIZE', defaultMessage: 'Authorize' })}
                    {showAuth ? (
                        <ChevronDownIcon style={{ width: 16, height: 16, color: '#6b7280' }} />
                    ) : (
                        <ChevronRightIcon style={{ width: 16, height: 16, color: '#6b7280' }} />
                    )}
                </button>
                {showAuth && (
                    <div
                        className="mt-2 p-4 rounded-lg flex flex-col gap-3"
                        style={{ border: '1px solid #d1d5db', maxWidth: 600, backgroundColor: '#f9fafb' }}
                    >
                        <div>
                            <label className="text-xs font-semibold" style={{ color: '#374151' }}>
                                {intl.formatMessage({ id: 'LP_API_DOC_BEARER_TOKEN_LABEL', defaultMessage: 'Bearer Token (JWT)' })}
                            </label>
                            <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>
                                <FormattedMessage
                                    id="LP_API_DOC_BEARER_TOKEN_HELP"
                                    defaultMessage="Use the {tokenEndpoint} endpoint to obtain a token."
                                    values={{ tokenEndpoint: <code className="px-1 rounded" style={{ backgroundColor: '#e5e7eb' }}>/token/generate</code> }}
                                />
                            </p>
                        </div>
                        <input
                            type="text"
                            value={bearerToken}
                            onChange={(e) => setBearerToken(e.target.value)}
                            placeholder={intl.formatMessage({ id: 'LP_API_DOC_ENTER_JWT_PLACEHOLDER', defaultMessage: 'Enter JWT token' })}
                            className="px-3 py-2 rounded text-sm font-mono"
                            style={{ border: '1px solid #d1d5db', outline: 'none' }}
                        />
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setShowAuth(false)}
                                className="px-4 py-1.5 rounded text-sm font-semibold"
                                style={{
                                    backgroundColor: bearerToken ? '#16a34a' : '#d1d5db',
                                    color: bearerToken ? '#ffffff' : '#6b7280',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                {bearerToken ? intl.formatMessage({ id: 'LP_API_DOC_AUTHORIZED', defaultMessage: 'Authorized' }) : intl.formatMessage({ id: 'LP_CLOSE', defaultMessage: 'Close' })}
                            </button>
                            {bearerToken && (
                                <button
                                    type="button"
                                    onClick={() => setBearerToken('')}
                                    className="px-4 py-1.5 rounded text-sm font-medium"
                                    style={{ border: '1px solid #d1d5db', background: '#ffffff', cursor: 'pointer' }}
                                >
                                    {intl.formatMessage({ id: 'LP_API_DOC_CLEAR', defaultMessage: 'Clear' })}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-6">
                <button
                    type="button"
                    onClick={() => setShowLanguages(!showLanguages)}
                    className="flex items-center gap-2 px-4 py-2 rounded text-sm font-semibold"
                    style={{
                        border: '1px solid #d1d5db',
                        background: showLanguages ? '#f9fafb' : '#ffffff',
                        cursor: 'pointer',
                    }}
                >
                    <TranslateIcon style={{ width: 16, height: 16, color: '#6366f1' }} />
                    {intl.formatMessage({ id: 'LP_API_DOC_LANGUAGE_IDS', defaultMessage: 'Language IDs' })}
                    {showLanguages ? (
                        <ChevronDownIcon style={{ width: 16, height: 16, color: '#6b7280' }} />
                    ) : (
                        <ChevronRightIcon style={{ width: 16, height: 16, color: '#6b7280' }} />
                    )}
                </button>
                {showLanguages && (
                    <div
                        className="mt-2 rounded-lg overflow-hidden"
                        style={{ border: '1px solid #d1d5db', maxWidth: 600 }}
                    >
                        <div className="px-4 py-3" style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                            <p className="text-xs" style={{ color: '#6b7280' }}>
                                <FormattedMessage
                                    id="LP_API_DOC_LANGUAGE_IDS_HELP"
                                    defaultMessage="Use these IDs for the {languageIdParam} parameter in questionnaire, portrait, and answer endpoints."
                                    values={{ languageIdParam: <code className="px-1 rounded" style={{ backgroundColor: '#e5e7eb' }}>language_id</code> }}
                                />
                            </p>
                        </div>
                        <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f9fafb' }}>
                                    <th className="px-4 py-2 text-left text-xs font-semibold" style={{ color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: 60 }}>{intl.formatMessage({ id: 'LP_API_DOC_TABLE_HEADER_ID', defaultMessage: 'ID' })}</th>
                                    <th className="px-4 py-2 text-left text-xs font-semibold" style={{ color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{intl.formatMessage({ id: 'LP_API_DOC_TABLE_HEADER_LANGUAGE', defaultMessage: 'Language' })}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {supportedLanguages.map((lang) => (
                                    <tr key={lang.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td className="px-4 py-1.5 font-mono text-xs font-semibold" style={{ color: '#4338ca', backgroundColor: '#ffffff' }}>{lang.id}</td>
                                        <td className="px-4 py-1.5 text-xs" style={{ color: '#374151', backgroundColor: '#ffffff' }}>
                                            {lang.name}{lang.nativeName !== lang.name && <span style={{ color: '#6b7280' }}> ({lang.nativeName})</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {tags.map((tag) => (
                <div key={tag} className="mt-8">
                    <button
                        type="button"
                        onClick={() => toggleTag(tag)}
                        className="flex items-center gap-2 w-full text-left"
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                        }}
                    >
                        {expandedTags.has(tag) ? (
                            <ChevronDownIcon style={{ width: 20, height: 20, color: '#374151' }} />
                        ) : (
                            <ChevronRightIcon style={{ width: 20, height: 20, color: '#374151' }} />
                        )}
                        <h2 className="text-lg font-bold" style={{ color: '#111827' }}>{tag}</h2>
                    </button>

                    {expandedTags.has(tag) && (
                        <div className="mt-3 flex flex-col gap-2">
                            {endpointsByTag[tag].map((endpoint) => {
                                const isExpanded = expandedEndpoints.has(endpoint.id);
                                const isTryActive = tryItOutActive.has(endpoint.id);
                                const isLoading = loadingIds.has(endpoint.id);
                                const response = responses[endpoint.id];
                                const style = METHOD_STYLES[endpoint.method];
                                const pathParams = endpoint.parameters?.filter((p) => p.in === 'path') || [];
                                const queryParams = endpoint.parameters?.filter((p) => p.in === 'query') || [];
                                const allParams = [...pathParams, ...queryParams];

                                return (
                                    <div
                                        key={endpoint.id}
                                        className="rounded-lg overflow-hidden"
                                        style={cardStyle(endpoint.method)}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => toggleEndpoint(endpoint.id)}
                                            className="w-full flex items-center gap-3 p-3 text-left"
                                            style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                                        >
                                            <span
                                                className="px-3 py-1 rounded text-xs font-bold uppercase text-center"
                                                style={badgeStyle(endpoint.method)}
                                            >
                                                {endpoint.method}
                                            </span>
                                            <span
                                                className="font-mono text-sm font-semibold"
                                                style={{ color: style.text }}
                                            >
                                                {endpoint.path}
                                            </span>
                                            <span
                                                className="text-sm flex-1 truncate"
                                                style={{ color: '#4b5563' }}
                                            >
                                                {endpoint.summary}
                                            </span>
                                            {endpoint.requiresAuth && (
                                                <LockClosedIcon
                                                    style={{
                                                        width: 14, height: 14, color: '#9ca3af', flexShrink: 0,
                                                    }}
                                                />
                                            )}
                                            {isExpanded ? (
                                                <ChevronDownIcon style={{
                                                    width: 18, height: 18, color: '#9ca3af', flexShrink: 0,
                                                }} />
                                            ) : (
                                                <ChevronRightIcon style={{
                                                    width: 18, height: 18, color: '#9ca3af', flexShrink: 0,
                                                }} />
                                            )}
                                        </button>

                                        {isExpanded && (
                                            <div className="px-4 pb-4 pt-1">
                                                <p className="text-sm" style={{ color: '#4b5563' }}>
                                                    {endpoint.description}
                                                </p>

                                                {allParams.length > 0 && (
                                                    <div className="mt-4">
                                                        <h4 className="text-sm font-semibold mb-2" style={{ color: '#111827' }}>
                                                            {intl.formatMessage({ id: 'LP_API_DOC_PARAMETERS', defaultMessage: 'Parameters' })}
                                                        </h4>
                                                        <div
                                                            className="rounded overflow-hidden text-sm"
                                                            style={{ border: '1px solid #e5e7eb' }}
                                                        >
                                                            <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                                                                <thead>
                                                                    <tr style={{ backgroundColor: '#f9fafb' }}>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{intl.formatMessage({ id: 'LP_API_DOC_TABLE_HEADER_NAME', defaultMessage: 'Name' })}</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{intl.formatMessage({ id: 'LP_API_DOC_TABLE_HEADER_LOCATION', defaultMessage: 'Location' })}</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{intl.formatMessage({ id: 'LP_API_DOC_TABLE_HEADER_TYPE', defaultMessage: 'Type' })}</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{intl.formatMessage({ id: 'LP_API_DOC_TABLE_HEADER_DESCRIPTION', defaultMessage: 'Description' })}</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {allParams.map((param) => (
                                                                        <tr
                                                                            key={param.name}
                                                                            style={{ borderBottom: '1px solid #f3f4f6' }}
                                                                        >
                                                                            <td className="px-3 py-2 font-mono text-xs" style={{ backgroundColor: '#ffffff' }}>
                                                                                {param.name}
                                                                                {param.required && (
                                                                                    <span style={{ color: '#dc2626' }}>*</span>
                                                                                )}
                                                                            </td>
                                                                            <td className="px-3 py-2 text-xs" style={{ backgroundColor: '#ffffff', color: '#6b7280' }}>
                                                                                {param.in}
                                                                            </td>
                                                                            <td className="px-3 py-2 font-mono text-xs" style={{ backgroundColor: '#ffffff', color: '#059669' }}>
                                                                                {formatType(param.schema)}
                                                                            </td>
                                                                            <td className="px-3 py-2 text-xs" style={{ backgroundColor: '#ffffff', color: '#4b5563' }}>
                                                                                {param.description}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}

                                                {endpoint.requestBody && (
                                                    <div className="mt-4">
                                                        <h4 className="text-sm font-semibold mb-2" style={{ color: '#111827' }}>
                                                            {intl.formatMessage({ id: 'LP_API_DOC_REQUEST_BODY', defaultMessage: 'Request Body' })}
                                                            <span className="font-normal ml-2 text-xs" style={{ color: '#6b7280' }}>
                                                                {endpoint.requestBody.mediaType}
                                                            </span>
                                                        </h4>
                                                        <div
                                                            className="p-3 rounded"
                                                            style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}
                                                        >
                                                            <SchemaTree schema={endpoint.requestBody.schema} />
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="mt-4 flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => activateTryItOut(endpoint)}
                                                        className="px-4 py-1.5 rounded text-xs font-semibold"
                                                        style={{
                                                            border: `1px solid ${isTryActive ? '#dc2626' : style.badgeBg}`,
                                                            color: isTryActive ? '#dc2626' : style.badgeBg,
                                                            backgroundColor: isTryActive ? '#fef2f2' : '#ffffff',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        {isTryActive ? intl.formatMessage({ id: 'LP_CANCEL', defaultMessage: 'Cancel' }) : intl.formatMessage({ id: 'LP_API_DOC_TRY_IT_OUT', defaultMessage: 'Try it out' })}
                                                    </button>
                                                </div>

                                                {isTryActive && (
                                                    <div
                                                        className="mt-4 p-4 rounded-lg"
                                                        style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}
                                                    >
                                                        {allParams.length > 0 && (
                                                            <div className="mb-4">
                                                                <h5 className="text-xs font-semibold mb-2" style={{ color: '#374151' }}>
                                                                    {intl.formatMessage({ id: 'LP_API_DOC_PARAMETERS', defaultMessage: 'Parameters' })}
                                                                </h5>
                                                                <div className="flex flex-col gap-2">
                                                                    {allParams.map((param) => (
                                                                        <div key={param.name} className="flex flex-col gap-1">
                                                                            <label className="text-xs font-mono" style={{ color: '#374151' }}>
                                                                                {param.name}
                                                                                {param.required && <span style={{ color: '#dc2626' }}>*</span>}
                                                                                <span style={{ color: '#9ca3af' }}>{` (${param.in})`}</span>
                                                                            </label>
                                                                            {param.schema.enum ? (
                                                                                <select
                                                                                    value={paramValues[endpoint.id]?.[param.name] || ''}
                                                                                    onChange={(e) => updateParam(endpoint.id, param.name, e.target.value)}
                                                                                    className="px-3 py-1.5 rounded text-sm font-mono"
                                                                                    style={{ border: '1px solid #d1d5db', outline: 'none', backgroundColor: '#ffffff' }}
                                                                                >
                                                                                    <option value="">{intl.formatMessage({ id: 'LP_API_DOC_SELECT_PLACEHOLDER', defaultMessage: 'Select...' })}</option>
                                                                                    {param.schema.enum.map((v) => (
                                                                                        <option key={String(v)} value={String(v)}>{String(v)}</option>
                                                                                    ))}
                                                                                </select>
                                                                            ) : (
                                                                                <input
                                                                                    type="text"
                                                                                    value={paramValues[endpoint.id]?.[param.name] || ''}
                                                                                    onChange={(e) => updateParam(endpoint.id, param.name, e.target.value)}
                                                                                    placeholder={
                                                                                        param.schema.examples?.[0] !== undefined
                                                                                            ? String(param.schema.examples[0])
                                                                                            : param.schema.format || param.schema.type
                                                                                    }
                                                                                    className="px-3 py-1.5 rounded text-sm font-mono"
                                                                                    style={{ border: '1px solid #d1d5db', outline: 'none' }}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {endpoint.requestBody && (
                                                            <div className="mb-4">
                                                                <h5 className="text-xs font-semibold mb-2" style={{ color: '#374151' }}>
                                                                    {intl.formatMessage({ id: 'LP_API_DOC_REQUEST_BODY', defaultMessage: 'Request Body' })}
                                                                </h5>
                                                                <textarea
                                                                    value={bodyValues[endpoint.id] || ''}
                                                                    onChange={(e) => setBodyValues((prev) => ({
                                                                        ...prev,
                                                                        [endpoint.id]: e.target.value,
                                                                    }))}
                                                                    rows={Math.min(
                                                                        (bodyValues[endpoint.id] || '').split('\n').length + 1,
                                                                        20
                                                                    )}
                                                                    className="w-full px-3 py-2 rounded text-sm font-mono"
                                                                    style={{
                                                                        border: '1px solid #d1d5db',
                                                                        outline: 'none',
                                                                        resize: 'vertical',
                                                                        backgroundColor: '#1f2937',
                                                                        color: '#d1fae5',
                                                                    }}
                                                                />
                                                            </div>
                                                        )}

                                                        <div className="flex gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => executeRequest(endpoint)}
                                                                disabled={isLoading}
                                                                className="px-6 py-2 rounded text-sm font-bold"
                                                                style={{
                                                                    backgroundColor: style.badgeBg,
                                                                    color: '#ffffff',
                                                                    border: 'none',
                                                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                                                    opacity: isLoading ? 0.6 : 1,
                                                                }}
                                                            >
                                                                {isLoading ? intl.formatMessage({ id: 'LP_API_DOC_SENDING', defaultMessage: 'Sending...' }) : intl.formatMessage({ id: 'LP_API_DOC_EXECUTE', defaultMessage: 'Execute' })}
                                                            </button>
                                                            {response && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setResponses((prev) => {
                                                                        const next = { ...prev };
                                                                        delete next[endpoint.id];
                                                                        return next;
                                                                    })}
                                                                    className="px-4 py-2 rounded text-sm font-medium"
                                                                    style={{ border: '1px solid #d1d5db', background: '#ffffff', cursor: 'pointer' }}
                                                                >
                                                                    {intl.formatMessage({ id: 'LP_API_DOC_CLEAR', defaultMessage: 'Clear' })}
                                                                </button>
                                                            )}
                                                        </div>

                                                        {response && (
                                                            <div className="mt-4">
                                                                <h5 className="text-xs font-semibold mb-2" style={{ color: '#374151' }}>
                                                                    {intl.formatMessage({ id: 'LP_API_DOC_SERVER_RESPONSE', defaultMessage: 'Server Response' })}
                                                                </h5>
                                                                <div className="flex items-center gap-3 mb-2">
                                                                    <span
                                                                        className="text-sm font-bold"
                                                                        style={{ color: response.status === 0 ? '#dc2626' : getStatusColor(String(response.status)) }}
                                                                    >
                                                                        {response.status === 0 ? intl.formatMessage({ id: 'LP_API_DOC_ERROR', defaultMessage: 'Error' }) : response.status} {response.statusText}
                                                                    </span>
                                                                    <span className="text-xs" style={{ color: '#9ca3af' }}>
                                                                        {response.duration}ms
                                                                    </span>
                                                                </div>
                                                                <pre
                                                                    className="p-3 rounded text-xs overflow-auto"
                                                                    style={{
                                                                        backgroundColor: '#1f2937',
                                                                        color: '#d1fae5',
                                                                        maxHeight: 400,
                                                                        whiteSpace: 'pre-wrap',
                                                                        wordBreak: 'break-word',
                                                                    }}
                                                                >
                                                                    {response.body}
                                                                </pre>

                                                                <div className="mt-3">
                                                                    <h5 className="text-xs font-semibold mb-1" style={{ color: '#374151' }}>
                                                                        {intl.formatMessage({ id: 'LP_API_DOC_CURL', defaultMessage: 'Curl' })}
                                                                    </h5>
                                                                    <pre
                                                                        className="p-3 rounded text-xs overflow-auto"
                                                                        style={{
                                                                            backgroundColor: '#111827',
                                                                            color: '#e5e7eb',
                                                                            whiteSpace: 'pre-wrap',
                                                                            wordBreak: 'break-word',
                                                                        }}
                                                                    >
                                                                        {buildCurl(
                                                                            endpoint.method,
                                                                            buildEndpointUrl(endpoint),
                                                                            endpoint.requiresAuth ? bearerToken : '',
                                                                            endpoint.requestBody?.mediaType,
                                                                            bodyValues[endpoint.id]
                                                                        )}
                                                                    </pre>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                <div className="mt-4">
                                                    <h4 className="text-sm font-semibold mb-2" style={{ color: '#111827' }}>
                                                        {intl.formatMessage({ id: 'LP_API_DOC_RESPONSES', defaultMessage: 'Responses' })}
                                                    </h4>
                                                    <div className="flex flex-col gap-1">
                                                        {endpoint.responses.map((resp) => (
                                                            <div
                                                                key={resp.statusCode}
                                                                className="rounded p-3"
                                                                style={{
                                                                    backgroundColor: '#ffffff',
                                                                    border: '1px solid #e5e7eb',
                                                                }}
                                                            >
                                                                <div className="flex items-start gap-3">
                                                                    <span
                                                                        className="text-xs font-bold font-mono"
                                                                        style={{ color: getStatusColor(resp.statusCode), minWidth: 28 }}
                                                                    >
                                                                        {resp.statusCode}
                                                                    </span>
                                                                    <span className="text-xs" style={{ color: '#4b5563' }}>
                                                                        {resp.description}
                                                                    </span>
                                                                </div>
                                                                {resp.schema && resp.schema.properties && (
                                                                    <div className="mt-2 ml-10">
                                                                        <div
                                                                            className="p-2 rounded"
                                                                            style={{ backgroundColor: '#f9fafb' }}
                                                                        >
                                                                            <SchemaTree schema={resp.schema} />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {resp.mediaType && resp.mediaType !== 'application/json' && (
                                                                    <div className="mt-1 ml-10">
                                                                        <span className="text-xs font-mono" style={{ color: '#9ca3af' }}>
                                                                            Content-Type: {resp.mediaType}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default ApiDocumentationPage;
